import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useStripe, useElements, PaymentElement } from '@stripe/react-stripe-js';

import { Form } from 'react-form';
import * as queryString from 'query-string';
import PropTypes from 'prop-types';

import moment from 'moment';
import requireAuth from '../hocs/requireAuth';
import { required } from '../../utils/validation';

import * as checkoutEventRegistrationApi from '../../api/checkoutEventRegistrationApi';

import { getApiAxiosInstance } from '../../utils/network';
import { interpretStripeCardError } from '../../utils/payments';
import { eventFlows, registrationUserOptions, regStepIds } from '../../utils/eventConstants';
import { getUsapMessageFromError } from '../../utils/usap';

import { gtag } from '../Analytics/gtag';
import { Input } from '../Form';
import { showErrorPopup } from '../../actions/notification-actions';
import { trackEvent } from '../Analytics/TrackedClick';

const EventRegistrationCheckoutFormLegacy = (props) => {
    const {
        checkoutRequest,
        setCheckoutSubmittingFlag,
        submitting,
        currencySymbol,
        eventId,
        eventFee,
        auth,
        isAddingCardPayment,
        sidebarFlow,
        setSwitchApiResponse,
        multipleDivisionRegistrations,
        getMultiRegistrationTotal,
        registrationTypeForCamps,
        handleCloseClick,
     } = props;

    const dispatch = useDispatch();
    const stripe = useStripe();
    const elements = useElements();
    const [loadError, setLoadError] = useState(null);
    const [stripePaymentError, setStripePaymentError] = useState(null);

    const parsed = queryString.parse(props.location.search);
    const { jwt } = parsed;
    const isSwitchFlow = sidebarFlow === regStepIds.DIVISIONS_SWITCH;
    const isCampFlow = sidebarFlow === eventFlows.CAMP_FLOW;

    const displayTotalFee = () => {
        if (isCampFlow) {
            return getMultiRegistrationTotal();
        }
            return `${currencySymbol}${eventFee > 0 && eventFee?.toFixed(2)}`;
    };

    const validateCardholderName = (value) => {
        const incompleteError = !required(value) ? '' : 'You must provide a name.';
        return incompleteError;
    };

    const eventRegistrationAndPayment = async (paymentRequestResult = null) => {
        let res;
        let paymentIntentId;

        const axiosInstance = getApiAxiosInstance(jwt);
        try {
            if (isSwitchFlow) {
                res = await checkoutEventRegistrationApi.legacySwitchDivisions(axiosInstance, eventId, checkoutRequest);
                ({ paymentIntentId } = res.data.newDivisionRegistration);
                setSwitchApiResponse(res.data);
            } else if (isCampFlow) {
                // Mapping the camp flow data into a new object
                const mappedCampFlowData = {
                    // Creating a new key `childDivisionRegistrationRequests` with a transformed array
                    childDivisionRegistrationRequests: Object.entries(multipleDivisionRegistrations)
                    // Filtering out entries where quantity is greater than 0 since we filter the now new array  of [ [key, value]]
                    .filter(([, value]) => value.quantity > 0)
                    // Flattening and mapping the filtered entries
                    .flatMap(([key, value]) => value.playerNames.map(playerName => ({
                        tmsEventDivisionId: Number(key), // Convert `tmsEventDivisionId` from string to number
                        playerName, // Assign `playerName` from the array
                        }))),
                };
                res = await checkoutEventRegistrationApi.legacyMultiRegistration(axiosInstance, eventId, mappedCampFlowData);
                ({ clientSecret: paymentIntentId } = res.data);
            } else {
                res = await checkoutEventRegistrationApi.legacyMemberRegistrationCheckout(axiosInstance, eventId, checkoutRequest);
                ({ paymentIntentId } = res.data);
            }
        } catch (error) {
            handleCloseClick();

            const defaultMessage = 'Error completing registration';
            const retMessage = (error?.response?.data?.Message?.indexOf('suspended') > -1) ? error.response.data.Message : defaultMessage;
            const usapValidationMessage = getUsapMessageFromError(error);
            const retError = error?.response?.data?.DisplayMessage || usapValidationMessage || error?.response?.data?.Message || defaultMessage;

            trackEvent(`eventRegistrationAndPayment`, {
                page: 'EventRegistrationCheckoutFormLegacy',
                errorMessage: retMessage,
                action: `Error-${retError}`,
                memberId: auth.user.id,
                eventId,
                client: 'Web',
                paymentIntentId,
                timestamp: moment.utc(),
            });

            dispatch(showErrorPopup({
                message: retMessage,
                error: retError,
            }));

            if (paymentRequestResult) {
                paymentRequestResult.complete('fail');
            }

            // ... why was this still executing? lmao
            setCheckoutSubmittingFlag(false);
            return;
        }

        const { error: stripeCardError } = await stripe.confirmPayment({
            elements,
            clientSecret: paymentIntentId,
            confirmParams: {
                // This is a required parameter for the confirmPayment method, but we don't need it for our use case at the moment
                return_url: `https://example.com/order/123/complete`,
            },
            redirect: 'if_required',
        });

        if (stripeCardError) {
            const errorMessage = interpretStripeCardError(stripeCardError);

            trackEvent(`stripeCardError`, {
                page: 'EventRegistrationCheckoutFormLegacy',
                stripeCardError,
                action: `Error-${errorMessage}`,
                memberId: auth.user.id,
                eventId,
                client: 'Web',
                paymentIntentId,
                timestamp: moment.utc(),
            });

            setCheckoutSubmittingFlag(false);
            setStripePaymentError(errorMessage);
            return;
        }

        if (paymentRequestResult) {
            paymentRequestResult.complete('success');
        }

        props.stripePaymentSuccessWrapUp(res.data);
        setCheckoutSubmittingFlag(false);
    };

    const handleSubmit = async () => {
        setStripePaymentError(null);
        if (!stripe || !elements) {
            // Stripe.js has not loaded yet. Make sure to disable
            // form submission until Stripe.js has loaded.
            return;
        }
        // Trigger form validation and wallet collection
        const { error: submitError } = await elements.submit();

        if (submitError) {
            // Stripe element handles displaying the error, so we don't need to do anything here
            return;
        }

        setCheckoutSubmittingFlag(true);
        gtag('event', {
            category: 'Payment Info Entered',
            context: `eventId: ${eventId}`,
        });
        // adding track calls to check impact of save card feature - mapped to GA4
        if (window && window.analytics) {
            const { MULTIPLE_PLAYERS, SINGLE_PLAYER } = registrationUserOptions;
            const registrationType = registrationTypeForCamps === MULTIPLE_PLAYERS ? MULTIPLE_PLAYERS : SINGLE_PLAYER;

            window.analytics.track('Payment Info Entered', {
                userId: auth.user.id,
                eventId,
                registrationType,
            });
        }
        // creating a private function because same code is required for apple/google pay
        await eventRegistrationAndPayment(null);
    };
    const handleOnPaymentElementLoadError = (e) => {
        setLoadError(e?.error?.message);
    };

    const StripeLoadError = () => {
        return (
            <div className="payment-error-container">
                <p className="text-bold text-dark mb16" data-testid="event-reg.checkout.p-324">Payment system temporarily unavailable</p>
                <p data-testid="event-reg.checkout.p-325">We are having trouble loading our payment form. Please try:</p>
                <ul>
                    <li>Refreshing the page</li>
                    <li>Checking your internet connection</li>
                    <li>Trying again in a few moments</li>
                </ul>
                <button
                    className="btn btn-xl btn-primary text-uppercase font-weight-normal w-100 mt16 mb16"
                    type="button"
                    onClick={() => window.location.reload()}
                >
                    Reload Page
                </button>
                <p className="error-text" data-testid="event-reg.checkout.p-338">
                    Error: {loadError || 'Unknown payment processing error'}
                </p>
            </div>
        );
    };

    return (
        <div>
            {loadError ? (
                <StripeLoadError />
            ) : (
                <Form onSubmit={handleSubmit}>
                    {(formApi) => {
                        const { submitForm } = formApi;
                        return (
                            <form onSubmit={submitForm} id="EventRegistrationCheckoutFormLegacy" data-testid="event-reg.checkout.form-356">
                                <div>
                                    <div className={`row`}>
                                        <Input
                                            id={`cardholder-name`}
                                            field="name"
                                            label={`Cardholder Name`}
                                            formGroupCssClass="col-12"
                                            placeholder="ex. Serena Williams"
                                            validate={value => validateCardholderName(value)}
                                            disableProfanityCheck={true}
                                            data-testid="eventregistrationcheckoutformlegacy.cardholder-name-input"
                                        />
                                    </div>
                                    <div className={`row`}>
                                        <div className={`form-group form-group-xl col-12 mb16`}>
                                            <PaymentElement
                                                options={{
                                                    layout: 'auto',
                                                    paymentMethodOrder: ['card'],
                                                    wallets: {
                                                        googlePay: 'never',
                                                        applePay: 'never',
                                                        link: 'never',
                                                    },
                                                }}
                                                onLoadError={handleOnPaymentElementLoadError}
                                            />
                                        </div>
                                        {stripePaymentError && (
                                            <div className="pl16 pr16 mb16">
                                                <p className="error-text" data-testid="event-reg.checkout.p-406">
                                                    {stripePaymentError}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                                <div>
                                    <button
                                        type="submit"
                                        className={`btn btn-xl btn-primary text-uppercase font-weight-normal w-100 ${!stripe || submitting ? 'text-dark' : ''}`}
                                        disabled={!stripe || submitting}
                                        data-testid="eventregistrationcheckoutformlegacy.submit-btn"
                                    >
                                        {/* adding isAddingCardPayment condition to show Add or Pay text and hide amount */}
                                        {submitting && (<i className="fas fa-circle-notch fa-spin" />)} {isAddingCardPayment ? 'Save' : 'Pay'} {displayTotalFee()}
                                    </button>
                                </div>
                            </form>
                        );
                    }}
                </Form>
            )}
        </div>
    );
};

EventRegistrationCheckoutFormLegacy.propTypes = {
    location: PropTypes.shape({ search: PropTypes.string }).isRequired,
    sidebarFlow: PropTypes.string,
    setSwitchApiResponse: PropTypes.func.isRequired,
    getMultiRegistrationTotal: PropTypes.func.isRequired,
    multipleDivisionRegistrations: PropTypes.objectOf(PropTypes.shape({
        quantity: PropTypes.number,
        playerNames: PropTypes.arrayOf(PropTypes.string),
    })).isRequired,
    registrationTypeForCamps: PropTypes.string.isRequired,
    eventFee: PropTypes.number.isRequired,
};

EventRegistrationCheckoutFormLegacy.defaultProps = {
    sidebarFlow: '',
};

export default requireAuth(EventRegistrationCheckoutFormLegacy);
